<template>
  <div
    id="paginacao"
    v-show="totalPaginas > 1"
  >
    <button
      @click="mudarPagina(paginaAtualEfetiva - 1)"
      :disabled="paginaAtualEfetiva === 0"
    >
      Voltar
    </button>

    <div class="paginas">
      <!-- Primeira página sempre visível -->
      <div
        class="paginas-disponiveis"
        :class="{ 'pagina-selecionada': paginaAtualEfetiva === 0 }"
        @click="mudarPagina(0)"
      >
        1
      </div>

      <!-- Reticências à esquerda -->
      <div
        class="sinal-pagina"
        v-if="mostrarReticenciasEsquerda"
      >
        ...
      </div>

      <!-- Páginas do meio -->
      <div
        v-for="pagina in paginasVisiveis"
        :key="pagina"
        class="paginas-disponiveis"
        :class="{ 'pagina-selecionada': paginaAtualEfetiva === pagina - 1 }"
        @click="mudarPagina(pagina - 1)"
      >
        {{ pagina }}
      </div>

      <!-- Reticências à direita -->
      <div
        class="sinal-pagina"
        v-if="mostrarReticenciasDireita"
      >
        ...
      </div>

      <!-- Última página sempre visível (só mostra se houver mais de 1 página) -->
      <div
        v-if="totalPaginas > 1"
        class="paginas-disponiveis"
        :class="{
          'pagina-selecionada': paginaAtualEfetiva === totalPaginas - 1,
        }"
        @click="mudarPagina(totalPaginas - 1)"
      >
        {{ totalPaginas }}
      </div>
    </div>

    <button
      @click="mudarPagina(paginaAtualEfetiva + 1)"
      :disabled="paginaAtualEfetiva >= totalPaginas - 1"
    >
      Avançar
    </button>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";

const props = defineProps({
  totalPaginas: {
    type: Number,
    required: true,
  },
  intervalo: {
    type: Number,
    default: 3, // Quantas páginas mostrar ao redor da atual
  },
  paginaAtual: {
    type: Number,
    default: undefined,
  },
});

const route = useRoute();

const paginaAtualEfetiva = computed(() => {
  if (props.paginaAtual !== undefined && props.paginaAtual !== null) {
    return props.paginaAtual;
  }
  return Number(route.query.page ?? 0);
});

const emit = defineEmits(["mudar-pagina"]);

function mudarPagina(pagina) {
  if (pagina < 0 || pagina >= props.totalPaginas) return;

  emit("mudar-pagina", pagina);

  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}

// Páginas visíveis ao redor da página atual (excluindo primeira e última)
const paginasVisiveis = computed(() => {
  const inicio = Math.max(2, paginaAtualEfetiva.value - props.intervalo + 1);
  const fim = Math.min(
    props.totalPaginas - 1,
    paginaAtualEfetiva.value + props.intervalo + 1
  );
  const paginas = [];
  for (let i = inicio; i <= fim; i++) {
    paginas.push(i);
  }
  return paginas;
});

const mostrarReticenciasEsquerda = computed(() => {
  return paginasVisiveis.value.length > 0 && paginasVisiveis.value[0] > 2;
});

const mostrarReticenciasDireita = computed(() => {
  const ultima = paginasVisiveis.value[paginasVisiveis.value.length - 1];
  return paginasVisiveis.value.length > 0 && ultima < props.totalPaginas - 1;
});
</script>

<style scoped>
#paginacao {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 20px 0;
}

#paginacao button {
  padding: 6px 18px;
  border-radius: 8px;
  background-color: #362065;
  color: white;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
}

#paginacao button:hover:not(:disabled) {
  background-color: #7349db;
}

#paginacao button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.paginas {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.paginas-disponiveis {
  padding: 5px;
  border-radius: 50%;
  background-color: #362065;
  color: white;
  height: 40px;
  width: 40px;
  font-size: 15px;
  text-align: center;
  align-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.paginas-disponiveis:hover {
  background-color: #7349db;
}

.pagina-selecionada {
  background-color: #7349db;
}

.sinal-pagina {
  color: #362065;
  font-weight: bold;
  padding: 0 4px;
  user-select: none;
}
</style>
